/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/*
 * Referenced from http://www.javascripter.net/faq/readingacookie.htm
 */

/**
 * Build regular expression to extract cookie value
 * @param name    cookie name
 * @param valueRE string for valid value regular expression
 */
const cookieRE = (name: string, valueRE: string = '.*?') => new RegExp(`(?:^|;)\\x20?${name}=(${valueRE})(?:;|$)`);

/* List of internal COOKIE and RE strings for their values */
enum COOKIE {
    MRHSESSION = 'MRHSession',
    LASTMRH_SESSION = 'LastMRH_Session',

    APM_SWG_LASTMRH_SESSSION = 'apm_swg_LastMRH_Session',
    APM_SWG_CATEGORY = 'apm_swg_category',

    APM_APPLICATION_NAME = 'apm_application_name',
    APM_APPLICATION_FAMILY = 'apm_application_family',

    APM_LOG = 'apm_log',
}

const internalCookies = {
    [COOKIE.MRHSESSION]: cookieRE(COOKIE.MRHSESSION, '[0-9a-f]{32}'),
    [COOKIE.LASTMRH_SESSION]: cookieRE(COOKIE.LASTMRH_SESSION, '[0-9a-f]{5,12}'),
    [COOKIE.APM_SWG_LASTMRH_SESSSION]: cookieRE(COOKIE.APM_SWG_LASTMRH_SESSSION, '[0-9a-f]{5,12}'),
    [COOKIE.APM_SWG_CATEGORY]: cookieRE(COOKIE.APM_SWG_CATEGORY, '[a-zA-Z0-9/_() -]{1,64}'),
};

/**
 * Internal cookies cannot be accessed via get/set/delete functions;
 * Check cookie name and log error for internal cookies.
 * Returns 'true' if cookie name is internal.
 * @param name cookie name
 * @param op   name of operation (get/set/delete)
 */
function checkInternalCookies(name: string, op: string): boolean {
    if (internalCookies.hasOwnProperty(name)) {
        console.error(`Attempt to ${op} internal cookie ${name}`);
        return true;
    }
    return false;
}

/**
 * Get cookie value by name.
 * Internal cookies cannot be read by this function.
 * @param name cookie name
 */
function getCookie(name: string): string | null {
    if (checkInternalCookies(name, 'get')) {
        return null;
    }
    name = name.replace(/([.\\+\-*:\/?!|^${}()\[\]])/g, '\\$1');
    const results = document.cookie.match(cookieRE(name));
    return results ? decodeURIComponent(results[1]) : null;
}

/**
 * Set cookie for current domain.
 * Secure flag is set automatically for HTTPS pages.
 * Internal cookies cannot be changed by this function.
 * @param name    cookie name
 * @param value   cookie value
 * @param path    cookie path; '/' by default
 * @param expDays expiration time, in days; 0 by default (no expiration)
 */
function setCookie(name: string, value: string, path: string = '/', expDays: number = 0): void {
    if (checkInternalCookies(name, 'set')) {
        return;
    }
    const secure = window.location.protocol.toLowerCase() === 'https:';
    if (secure) {
        value += '; secure';
    }
    if (expDays !== 0) {
        const d = new Date();
        d.setTime(d.getTime() + expDays * 24 * 60 * 60 * 1000);
        value += '; expires=' + d.toUTCString();
    }
    document.cookie = name + '=' + value + (path ? '; path=' + path : '');
}

/**
 * Remove cookie.
 * Internal cookies cannot be removed by this function.
 * @param name   cookie name
 * @param path   cookie path; '/' by default
 * @param secure secure flag; not used by default.
 */
function deleteCookie(name: string, path: string = '/', secure?: boolean): void {
    if (checkInternalCookies(name, 'delete')) {
        return;
    }
    document.cookie =
        name +
        '=' +
        (path ? ';path=' + path : '') +
        (secure ? ';secure' : '') +
        '; expires=Thu, 01-Jan-1970 00:00:01 GMT';
}

/**
 * Get value of internal cookie.
 * The value is tested against RE configured.
 * 'null' is returned if the value is invalid
 * or cookie name is not equal to any internal cookie names.
 * @param name cookie name
 */
function getInternalCookie(name: string): string | null {
    if (internalCookies.hasOwnProperty(name)) {
        const results = document.cookie.match(internalCookies[name]);
        return results ? results[1] : null;
    }
    return null;
}

export { COOKIE, getCookie, setCookie, deleteCookie, getInternalCookie };
